<template>
  <!-- "Save to list": the compact dialog OnBoarding.vue shows for the AddTrailToList page,
       matching the apps' Save to list sheet. Only Add/Added and the bin act on a list; the
       rules (order, search, the Added filter, the note after Done) live in
       utils/helpers/saveToList.ts. -->
  <div ref="root" class="stl" tabindex="-1" @keydown.esc="onEscape">
    <SaveToListNewListPage
      v-if="page === 'new'"
      :hike-name="hikeName"
      :initial-name="query.trim()"
      :busy="creating"
      :error="errorMessage"
      @back="backToLists"
      @create="createList"
    />
    <template v-else>
      <div class="stl-handle" aria-hidden="true"></div>
      <div class="stl-head">
        <div class="stl-grow">
          <h2 id="saveToListTitle" class="stl-title">{{ $t('save_to_list.title') }}</h2>
          <div v-if="hikeName" class="stl-subtitle">{{ hikeName }}</div>
        </div>
        <button class="stl-btn stl-btn-sm stl-btn-primary" type="button" data-test="save-to-list-done" @click="done">
          {{ $t('save_to_list.done') }}
        </button>
      </div>

      <div class="stl-tools">
        <label class="stl-search">
          <span class="ms" aria-hidden="true">search</span>
          <input
            ref="search"
            v-model="query"
            type="search"
            :placeholder="$t('save_to_list.search_placeholder')"
            :aria-label="$t('save_to_list.search_placeholder')"
            autocomplete="off"
            data-test="save-to-list-search"
            @input="onSearch"
          >
          <button
            v-if="query"
            class="stl-icon-btn stl-clear"
            type="button"
            :aria-label="$t('save_to_list.clear_search')"
            data-test="save-to-list-clear"
            @click="clearSearch"
          >
            <span class="ms fill" aria-hidden="true">cancel</span>
          </button>
        </label>
        <div class="stl-chips" role="group" :aria-label="$t('save_to_list.show')">
          <button
            class="stl-chip"
            :class="{ on: filter === 'all' }"
            type="button"
            :aria-pressed="filter === 'all'"
            data-test="save-to-list-filter-all"
            @click="setFilter('all')"
          >{{ $t('save_to_list.filter_all') }}</button>
          <button
            class="stl-chip"
            :class="{ on: filter === 'added' }"
            type="button"
            :aria-pressed="filter === 'added'"
            data-test="save-to-list-filter-added"
            @click="setFilter('added')"
          >{{ $t('save_to_list.filter_added') }} <span class="n">{{ view.addedCount }}</span></button>
        </div>
      </div>

      <div class="stl-body">
        <div v-if="errorMessage" class="stl-error" role="alert" data-test="save-to-list-error">{{ errorMessage }}</div>
        <div v-if="loading" class="stl-empty">{{ $t('save_to_list.loading') }}</div>
        <template v-else>
          <button v-if="view.newListAtTop" class="stl-new-row" type="button" data-test="save-to-list-new" @click="openNewList">
            <span class="tile"><span class="ms" aria-hidden="true">add</span></span>{{ $t('save_to_list.new_list') }}
          </button>
          <div v-if="view.heading === 'your_lists'" class="stl-label">{{ $t('save_to_list.your_lists') }}</div>
          <div v-if="view.heading === 'added_to'" class="stl-label">{{ $t('save_to_list.added_to') }}</div>
          <div v-if="view.matchCount !== null" class="stl-match" data-test="save-to-list-match">
            <template v-if="view.matchCount === 0">{{ $t('save_to_list.no_matches', { query: query.trim() }) }}</template>
            <i18n-t v-else-if="view.matchCount === 1" keypath="save_to_list.matches_one" tag="span" scope="global">
              <template #lists><b>{{ $t('save_to_list.lists_one') }}</b></template>
              <template #query>{{ query.trim() }}</template>
            </i18n-t>
            <i18n-t v-else keypath="save_to_list.matches_many" tag="span" scope="global">
              <template #lists><b>{{ $t('save_to_list.lists_many', { count: view.matchCount }) }}</b></template>
              <template #query>{{ query.trim() }}</template>
            </i18n-t>
          </div>

          <div v-if="view.rows.length" class="stl-group">
            <template v-for="list in view.rows" :key="list.id">
              <div class="stl-row" data-test="save-to-list-row">
                <span class="stl-thumb" :class="thumbClass(list)">
                  <img v-if="thumbnail(list).kind === 'photo'" :src="thumbnail(list).url" alt="" loading="lazy">
                  <span v-else-if="thumbnail(list).kind === 'completed'" class="ms" aria-hidden="true">check</span>
                  <span v-else-if="thumbnail(list).kind === 'favourites'" class="ms fill" aria-hidden="true">favorite</span>
                  <span v-else class="ms fill" aria-hidden="true">landscape</span>
                </span>
                <span class="stl-txt">
                  <span class="stl-name">{{ list.name }}</span>
                  <span class="stl-sub">
                    {{ countText(list) }} ·
                    <template v-if="list.publiclyVisible"><span class="ms" aria-hidden="true">public</span>{{ $t('save_to_list.everyone') }}</template>
                    <template v-else><span class="ms fill" aria-hidden="true">lock</span>{{ $t('save_to_list.only_you') }}</template>
                  </span>
                </span>
                <!-- One button that changes its face, so keyboard focus stays on it. -->
                <button
                  class="stl-act"
                  :class="{ on: isAdded(list) }"
                  type="button"
                  :aria-pressed="isAdded(list) ? 'true' : 'false'"
                  :aria-label="isAdded(list) ? $t('save_to_list.remove_from', { name: list.name }) : $t('save_to_list.add_to', { name: list.name })"
                  :disabled="isPending(list)"
                  data-test="toggle-hike-list"
                  @click="tapList(list)"
                ><span class="ms" aria-hidden="true">{{ isAdded(list) ? 'check' : 'add' }}</span>{{ isAdded(list) ? $t('save_to_list.added') : $t('save_to_list.add') }}</button>
                <button
                  v-if="isDeletable(list)"
                  class="stl-del"
                  type="button"
                  :aria-label="$t('save_to_list.delete_list_named', { name: list.name })"
                  :title="$t('save_to_list.delete_list')"
                  :disabled="isPending(list)"
                  data-test="delete-hike-list"
                  @click="deleteList(list)"
                ><span class="ms" aria-hidden="true">delete</span></button>
                <span v-else class="stl-del-gap"></span>
              </div>
              <div v-if="askingDateFor && sameId(askingDateFor.id, list.id)" class="stl-done-step" data-test="completed-date-step">
                <span class="q">{{ $t('hikes.complete_modal_title') }}</span>
                <input
                  v-model="completedOn"
                  class="stl-field"
                  type="date"
                  :max="today"
                  :aria-label="$t('hikes.complete_modal_label')"
                  data-test="completed-date-input"
                >
                <div class="acts">
                  <button class="stl-btn stl-btn-sm stl-btn-outline" type="button" data-test="completed-date-cancel" @click="cancelCompletedDate">
                    {{ $t('hikes.complete_modal_cancel') }}
                  </button>
                  <button class="stl-btn stl-btn-sm stl-btn-primary" type="button" :disabled="!completedAt" data-test="completed-date-confirm" @click="confirmCompletedDate">
                    {{ $t('save_to_list.mark_as_completed') }}
                  </button>
                </div>
              </div>
            </template>
          </div>
          <div v-else-if="view.showsAddedEmpty" class="stl-empty" data-test="save-to-list-added-empty">{{ isRecording ? $t('save_to_list.empty_added_recording') : $t('save_to_list.empty_added') }}</div>

          <button v-if="view.newListAfterResults" class="stl-new-row" type="button" data-test="save-to-list-new-named" @click="openNewList">
            <span class="tile"><span class="ms" aria-hidden="true">add</span></span>{{ $t('save_to_list.new_list_named', { name: view.newListAfterResults }) }}
          </button>
        </template>
        <div v-if="pendingDelete" class="stl-note-space"></div>
        <div class="stl-fade"></div>
      </div>

      <div v-if="pendingDelete" class="stl-note running" role="status" data-test="save-to-list-undo-note">
        <span class="msg">{{ $t('save_to_list.deleted', { name: pendingDelete.list.name }) }}</span>
        <button type="button" data-test="save-to-list-undo" @click="undoDelete">{{ $t('save_to_list.undo') }}</button>
        <span class="timer"></span>
      </div>
    </template>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { asksForCompletedDate, isHikeInList, sameHikeId } from '../../../utils/helpers/hikeListMembership';
import { completedAtFromDateInput, todayDateInputValue } from '../../../utils/helpers/completedAt';
import {
  doneNote,
  hikeCountLabel,
  isDeletableList,
  membershipSnapshot,
  orderForSaveToList,
  saveToListView,
  sessionChanges,
  thumbnailFor,
  withoutList,
} from '../../../utils/helpers/saveToList';
import {
  SAVE_TO_LIST_EVENTS,
  doneProperties,
  saveToListSource,
  trackSaveToList,
} from '../../../utils/helpers/saveToListAnalytics';
import SaveToListNewListPage from './SaveToListNewListPage.vue';

// How long Undo is offered after the bin, as in the apps.
export const UNDO_WINDOW_MS = 5000;
// Tells the hike page whether the hike is now in any list, for its Saved button.
export const SAVE_TO_LIST_CHANGED_EVENT = 'hiiker:save-to-list-changed';

export default defineComponent({
  components: { SaveToListNewListPage },
  data() {
    return {
      page: 'list',
      filter: 'all',
      query: '',
      // Lists the hike was taken out of under the Added filter, kept in view until the
      // filter or search changes.
      keepIds: [],
      loading: true,
      isOpen: false,
      initialMembership: {},
      createdIds: [],
      creating: false,
      searchTracked: false,
      pendingDelete: null,
      undoTimer: null,
      // The Completed list while its date is being asked for; null otherwise.
      askingDateFor: null,
      completedOn: todayDateInputValue(),
      today: todayDateInputValue(),
    };
  },
  computed: {
    hikeId() {
      return this.$store.state.onBoarding.hikeId;
    },
    hikeName() {
      return this.$store.state.onBoarding.hikeName;
    },
    // A recording sees only the user's own lists, each counted in items (hikes + recordings).
    isRecording() {
      return this.$store.state.onBoarding.itemKind === 'recording';
    },
    errorMessage() {
      return this.$store.state.onBoarding.errorMessage;
    },
    lists() {
      return orderForSaveToList(this.$store.state.onBoarding.usersHikeLists || []);
    },
    view() {
      return saveToListView({
        lists: this.lists,
        hikeId: this.hikeId,
        filter: this.filter,
        query: this.query,
        keepIds: this.keepIds,
      });
    },
    completedAt() {
      return completedAtFromDateInput(this.completedOn, this.today);
    },
  },
  mounted() {
    this.dialogEl = document.getElementById('on-boarding-dialog');
    this.dialogEl?.addEventListener('show.bs.modal', this.onOpen);
    this.dialogEl?.addEventListener('hide.bs.modal', this.finish);
    document.addEventListener('keydown', this.onDocumentKeydown);
    this.onOpen();
  },
  beforeUnmount() {
    this.dialogEl?.removeEventListener('show.bs.modal', this.onOpen);
    this.dialogEl?.removeEventListener('hide.bs.modal', this.finish);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    this.commitPendingDelete();
  },
  methods: {
    // Each time the dialog opens: a fresh view, every list loaded, and a note of which
    // lists already had the hike so Done can say what changed.
    async onOpen() {
      this.isOpen = true;
      this.page = 'list';
      this.filter = 'all';
      this.query = '';
      this.keepIds = [];
      this.askingDateFor = null;
      this.createdIds = [];
      this.searchTracked = false;
      this.loading = true;
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/setSaveToListNote', null);
      this.track(SAVE_TO_LIST_EVENTS.opened, {
        source: this.$store.state.onBoarding.saveToListSource || saveToListSource(window.location.pathname),
      });
      await this.$store.dispatch('onBoarding/getUserHikeLists');
      this.initialMembership = membershipSnapshot(this.$store.state.onBoarding.usersHikeLists || [], this.hikeId);
      this.loading = false;
    },
    // Done, Esc or a click outside: a pending delete goes through now, and the page says
    // what changed.
    finish() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.commitPendingDelete();
      this.askingDateFor = null;
      const lists = this.$store.state.onBoarding.usersHikeLists || [];
      const changes = sessionChanges(this.initialMembership, lists, this.hikeId);
      const createdNames = lists.filter((l) => this.createdIds.includes(String(l.id))).map((l) => l.name);
      const note = doneNote(changes);
      this.$store.commit('onBoarding/setSaveToListNote', note ? { kind: note.kind, text: this.noteText(note) } : null);
      this.track(SAVE_TO_LIST_EVENTS.done, doneProperties({
        added: changes.added.filter((name) => !createdNames.includes(name)).length,
        removed: changes.removed.length,
        created: this.createdIds.length,
      }));
      window.dispatchEvent(new CustomEvent(SAVE_TO_LIST_CHANGED_EVENT, {
        detail: {
          hikeId: this.hikeId,
          itemKind: this.$store.state.onBoarding.itemKind,
          saved: lists.some((l) => isHikeInList(l, this.hikeId)),
        },
      }));
    },
    done() {
      this.finish();
      this.$store.dispatch('onBoarding/hideModal');
    },
    onEscape(event) {
      // Esc on the New Hike List form goes back to the lists; on the lists it closes.
      if (this.page === 'new') {
        event.stopPropagation();
        this.backToLists();
      }
    },
    // Bootstrap only hears Esc from inside the modal. When the focused button went away (a
    // binned list, a closed form) focus falls back to the page, so handle Esc from there too.
    onDocumentKeydown(event) {
      if (event.key !== 'Escape' || !this.isOpen) return;
      if (this.dialogEl?.contains(event.target)) return;
      if (this.page === 'new') this.backToLists();
      else this.done();
    },
    // Keeps keyboard focus inside the dialog when the focused control goes away.
    focusDialog() {
      this.$nextTick(() => this.$refs.root?.focus({ preventScroll: true }));
    },
    noteText(note) {
      const names = note.names.key === 'one'
        ? note.names.first
        : note.names.key === 'two'
          ? this.$t('save_to_list.names_two', { first: note.names.first, second: note.names.second })
          : this.$t('save_to_list.names_more', { first: note.names.first, count: note.names.count });
      return note.kind === 'saved'
        ? this.$t('save_to_list.saved_to', { names })
        : this.$t('save_to_list.removed_from', { names });
    },
    // Every event says what was being saved, so one question splits hikes from recordings.
    track(event, props = {}) {
      trackSaveToList(event, { ...props, item: this.$store.state.onBoarding.itemKind || 'hike' });
    },
    countText(list) {
      const { key, count } = hikeCountLabel(list.hikesCount ?? list.hikes?.length);
      if (this.isRecording) {
        if (key === 'none') return this.$t('save_to_list.items_none');
        if (key === 'one') return this.$t('save_to_list.items_one');
        return this.$t('save_to_list.items_many', { count });
      }
      if (key === 'none') return this.$t('save_to_list.hikes_none');
      if (key === 'one') return this.$t('save_to_list.hikes_one');
      return this.$t('save_to_list.hikes_many', { count });
    },
    thumbnail(list) {
      return thumbnailFor(list);
    },
    thumbClass(list) {
      const { kind } = thumbnailFor(list);
      return { photo: kind === 'photo', ph: kind === 'placeholder' };
    },
    isAdded(list) {
      return isHikeInList(list, this.hikeId);
    },
    isDeletable(list) {
      return isDeletableList(list);
    },
    isPending(list) {
      return this.$store.state.onBoarding.pendingHikeListIds.some((id) => sameHikeId(id, list.id));
    },
    sameId(a, b) {
      return String(a) === String(b);
    },
    setFilter(filter) {
      this.filter = filter;
      this.keepIds = [];
      if (filter === 'added') this.track(SAVE_TO_LIST_EVENTS.filterAdded);
    },
    onSearch() {
      this.keepIds = [];
      if (!this.searchTracked && this.query.trim()) {
        this.searchTracked = true;
        this.track(SAVE_TO_LIST_EVENTS.searchUsed);
      }
    },
    clearSearch() {
      this.query = '';
      this.keepIds = [];
      this.$refs.search?.focus();
    },
    tapList(list) {
      if (asksForCompletedDate(list, this.hikeId)) {
        this.today = todayDateInputValue();
        this.completedOn = this.today;
        this.askingDateFor = list;
        return;
      }
      if (this.filter === 'added' && !this.keepIds.includes(String(list.id))) this.keepIds.push(String(list.id));
      this.$store.dispatch('onBoarding/toggleHikeInList', list);
    },
    confirmCompletedDate() {
      const { completedAt, askingDateFor: hikeList } = this;
      if (!completedAt || !hikeList) return;
      this.askingDateFor = null;
      this.$store.dispatch('onBoarding/completeHikeInList', { hikeList, completedAt });
    },
    cancelCompletedDate() {
      this.askingDateFor = null;
    },
    // The bin: the list leaves at once and Undo is offered for 5 seconds; it's only
    // deleted on the server when that note goes (or the dialog closes).
    deleteList(list) {
      this.commitPendingDelete();
      const { removed, index } = withoutList(this.$store.state.onBoarding.usersHikeLists || [], list.id);
      if (!removed) return;
      if (this.askingDateFor && this.sameId(this.askingDateFor.id, list.id)) this.askingDateFor = null;
      this.$store.commit('onBoarding/removeHikeList', list.id);
      this.pendingDelete = { list: removed, index };
      this.undoTimer = setTimeout(() => this.commitPendingDelete(), UNDO_WINDOW_MS);
      this.focusDialog();
    },
    undoDelete() {
      if (!this.pendingDelete) return;
      clearTimeout(this.undoTimer);
      this.undoTimer = null;
      this.$store.commit('onBoarding/restoreHikeList', this.pendingDelete);
      this.pendingDelete = null;
      this.track(SAVE_TO_LIST_EVENTS.deleteUndone);
    },
    async commitPendingDelete() {
      clearTimeout(this.undoTimer);
      this.undoTimer = null;
      const pending = this.pendingDelete;
      if (!pending) return;
      this.pendingDelete = null;
      const deleted = await this.$store.dispatch('onBoarding/deleteHikeList', pending);
      if (deleted) this.track(SAVE_TO_LIST_EVENTS.listDeleted);
    },
    openNewList() {
      this.commitPendingDelete();
      this.$store.commit('onBoarding/setError', null);
      this.page = 'new';
    },
    backToLists() {
      this.$store.commit('onBoarding/setError', null);
      this.page = 'list';
      this.focusDialog();
    },
    async createList(form) {
      if (this.creating) return;
      this.creating = true;
      this.$store.commit('onBoarding/setError', null);
      try {
        const created = await this.$store.dispatch('onBoarding/createHikeList', form);
        if (!created) return;
        this.createdIds.push(String(created.id));
        this.track(SAVE_TO_LIST_EVENTS.listCreated);
        this.page = 'list';
        this.query = '';
        this.filter = 'all';
        this.keepIds = [];
        this.focusDialog();
      } finally {
        this.creating = false;
      }
    },
  },
});
</script>

<style scoped>
/* The web app's own tokens (app.scss, OptionsMenuModal.vue, the hero search and filter
   pills), as in the reviewed design. */
.stl {
  --w-modal: #1c1e22;
  --w-gray-dark: #1f2026;
  --w-gray: #22242a;
  --w-raised: #2a2c34;
  --w-hover: #30323b;
  --w-border: #434343;
  --w-hair: rgba(255, 255, 255, 0.08);
  --w-muted: #b2b2b2;
  --w-placeholder: #898989;
  --w-yellow: #fddf00;
  --w-yellow-ink: #36220f;
  --w-chip-active: #fbcd01;
  --w-red-soft: #ff6b73;
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  color: #fff;
  font: 400 14px/1.4 Montserrat, sans-serif;
  text-align: left;
}

.ms {
  font-family: 'Material Symbols Outlined';
  font-weight: 400;
  font-style: normal;
  font-size: 20px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  direction: ltr;
  -webkit-font-smoothing: antialiased;
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
}
.ms.fill { font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24; }

.stl:focus { outline: none; }
.stl-handle { display: none; }
.stl-head { display: flex; align-items: center; gap: 12px; padding: 18px 18px 12px; flex: none; }
.stl-grow { flex: 1; min-width: 0; }
.stl-title { font: 700 17px/1.25 Montserrat, sans-serif; margin: 0; color: #fff; }
.stl-subtitle { font: 400 13px Montserrat, sans-serif; color: var(--w-muted); margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.stl-btn {
  border-radius: 100px;
  font: 600 14px Montserrat, sans-serif;
  border: 1px solid transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
}
.stl-btn-sm { height: 35px; padding: 0 16px; }
.stl-btn-primary { background: var(--w-yellow); color: var(--w-yellow-ink); }
.stl-btn-outline { background: transparent; color: #adb5bd; border-color: #6c757d; }
.stl-btn[disabled] { opacity: 0.45; cursor: default; }

.stl-icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 0;
  background: transparent;
  color: #fff;
  display: grid;
  place-items: center;
  cursor: pointer;
  flex: none;
  padding: 0;
}
.stl-icon-btn:hover { background: rgba(255, 255, 255, 0.06); }

.stl-tools { padding: 0 18px; display: grid; gap: 12px; flex: none; }
.stl-search {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  margin: 0;
  padding: 0 8px 0 16px;
  background: var(--w-gray);
  border: 1px solid var(--w-border);
  border-radius: 44px;
}
.stl-search:focus-within { background: var(--w-gray-dark); }
.stl-search > .ms { color: #fff; font-size: 21px; }
.stl-search input {
  flex: 1;
  min-width: 0;
  background: transparent;
  border: 0;
  color: #fff;
  font: 400 14px Montserrat, sans-serif;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
}
.stl-search input::-webkit-search-cancel-button { display: none; }
.stl-search input::placeholder { color: var(--w-placeholder); }
.stl-clear { width: 28px; height: 28px; }
.stl-clear .ms { font-size: 18px; color: var(--w-muted); }

.stl-chips { display: flex; gap: 8px; flex-wrap: wrap; }
.stl-chip {
  height: 34px;
  padding: 0 14px;
  border-radius: 100px;
  background: var(--w-gray-dark);
  border: 1px solid var(--w-hair);
  color: rgba(255, 255, 255, 0.7);
  font: 500 14px Montserrat, sans-serif;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.stl-chip.on { background: #2b2c34; border-color: var(--w-chip-active); color: #fff; }
.stl-chip .n { color: var(--w-muted); font-variant-numeric: tabular-nums; }

.stl-body {
  position: relative;
  overflow-y: auto;
  padding: 14px 18px 18px;
  display: grid;
  /* Rows keep their content's height. Without this the grid squeezed the lists' card
     (overflow: hidden makes its minimum height 0) to fit, so it clipped the lists and
     the area never had anything to scroll. */
  grid-auto-rows: max-content;
  gap: 10px;
  align-content: start;
  min-height: 0;
  flex: 1;
}
.stl-label { font: 700 11px Montserrat, sans-serif; letter-spacing: 0.1em; text-transform: uppercase; color: var(--w-muted); margin: 6px 2px 0; }
.stl-match { font: 400 13px Montserrat, sans-serif; color: var(--w-muted); margin: 2px 2px 0; }
.stl-match b { color: #fff; font-weight: 600; }
.stl-empty { color: var(--w-muted); font-size: 13px; padding: 14px 4px; }
.stl-error { background: rgba(219, 43, 53, 0.14); color: var(--w-red-soft); border-radius: 10px; padding: 10px 12px; font: 500 13px Montserrat, sans-serif; }

.stl-new-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: var(--w-gray);
  border: 0;
  color: #fff;
  font: 600 14px Montserrat, sans-serif;
  cursor: pointer;
  text-align: left;
  width: 100%;
  min-width: 0;
  overflow-wrap: anywhere;
}
.stl-new-row:hover { background: var(--w-hover); }
.stl-new-row .tile {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(253, 223, 0, 0.1);
  color: var(--w-yellow);
  display: grid;
  place-items: center;
  flex: none;
}

.stl-group { background: var(--w-gray); border-radius: 12px; overflow: hidden; }
.stl-row { display: flex; align-items: center; gap: 12px; padding: 10px 10px 10px 12px; }
.stl-row + .stl-row,
.stl-row + .stl-done-step,
.stl-done-step + .stl-row { border-top: 1px solid var(--w-hair); }

.stl-thumb {
  width: 40px;
  height: 40px;
  border-radius: 9px;
  flex: none;
  display: grid;
  place-items: center;
  background: var(--w-raised);
  color: #fff;
  overflow: hidden;
}
.stl-thumb.ph { color: #4a4c52; }
.stl-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }

.stl-txt { flex: 1; min-width: 0; display: grid; gap: 1px; }
.stl-name { font: 500 14px Montserrat, sans-serif; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.stl-sub {
  display: flex;
  align-items: center;
  gap: 4px;
  font: 400 12px Montserrat, sans-serif;
  color: var(--w-muted);
  white-space: nowrap;
  overflow: hidden;
  min-width: 0;
}
.stl-sub .ms { font-size: 13px; }

.stl-act {
  height: 32px;
  padding: 0 12px 0 9px;
  border-radius: 100px;
  font: 600 13px Montserrat, sans-serif;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  flex: none;
  background: var(--w-gray-dark);
  color: #fff;
  border: 1px solid var(--w-border);
  min-width: 80px;
  justify-content: center;
}
.stl-act .ms { font-size: 18px; }
.stl-act:hover { background: #333; }
.stl-act.on { background: var(--w-yellow); color: var(--w-yellow-ink); border-color: transparent; }
.stl-act.on:hover { background: #f2d400; }
.stl-act[disabled] { cursor: default; opacity: 0.7; }

.stl-del {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 0;
  background: transparent;
  color: #8a8d93;
  display: grid;
  place-items: center;
  cursor: pointer;
  flex: none;
  padding: 0;
}
.stl-del .ms { font-size: 20px; }
.stl-del:hover { background: rgba(219, 43, 53, 0.14); color: var(--w-red-soft); }
.stl-del-gap { width: 32px; flex: none; }

.stl-done-step { padding: 12px; display: grid; gap: 10px; background: #26282e; }
.stl-done-step .q { font: 600 13px Montserrat, sans-serif; }
.stl-done-step .acts { display: flex; gap: 8px; justify-content: flex-end; flex-wrap: wrap; }
.stl-field {
  background: var(--w-gray-dark);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #fff;
  font: 400 14px Montserrat, sans-serif;
  padding: 0 12px;
  height: 44px;
  width: 100%;
  color-scheme: dark;
}

.stl-btn:focus-visible,
.stl-icon-btn:focus-visible,
.stl-chip:focus-visible,
.stl-act:focus-visible,
.stl-del:focus-visible,
.stl-new-row:focus-visible,
.stl-note button:focus-visible,
.stl-search:focus-within { outline: 2px solid var(--w-yellow); outline-offset: 2px; }

.stl-fade { position: sticky; bottom: -18px; height: 28px; margin-top: -28px; background: linear-gradient(transparent, var(--w-modal)); pointer-events: none; }
.stl-note-space { height: 56px; }

/* The yellow note: the apps' in-app message, in the site's yellow and ink. */
.stl-note {
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 14px;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--w-yellow);
  color: var(--w-yellow-ink);
  border-radius: 10px;
  padding: 12px 14px;
  font: 700 14px/1.35 Montserrat, sans-serif;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}
.stl-note .msg { flex: 1; min-width: 0; overflow-wrap: anywhere; }
.stl-note button {
  margin-left: auto;
  background: transparent;
  border: 0;
  color: inherit;
  font: 700 14px Montserrat, sans-serif;
  text-decoration: underline;
  cursor: pointer;
  padding: 2px 4px;
}
.stl-note .timer { position: absolute; left: 0; bottom: 0; height: 3px; background: rgba(0, 0, 0, 0.3); width: 100%; transform-origin: left; }
.stl-note.running .timer { animation: stl-drain 5s linear forwards; }
@keyframes stl-drain { to { transform: scaleX(0); } }

/* Phones: the same content as a bottom sheet (OnBoarding.vue docks the dialog). */
@media (max-width: 575.98px) {
  .stl-handle { display: block; width: 36px; height: 4px; border-radius: 2px; background: #3a3c42; margin: 8px auto 0; flex: none; }
  .stl-head { padding-top: 10px; }
}

@media (prefers-reduced-motion: reduce) {
  .stl-note.running .timer { animation: none; }
}
</style>
